/**
 * Copy and data for the Sovereign Advisory site.
 * Sections import from here and do not hardcode visible strings.
 */

export const BOOKING_URL = 'https://calendar.app.google/zTvhRWFmZPedzbCE8'
export const EMAIL = 'hello@joinsov.com'
export const BRAND = 'Sovereign Advisory'
export const BOOK_INTRO = 'Book a free 30-minute call'
export const NAV_CTA = 'Book a call'

export const MENU = {
  open: 'Open menu',
  close: 'Close menu',
} as const

export const HERO_HEADLINES = [
  'Get sharp on AI. Then own it.',
  'An AI coach in your corner.',
  'Advice, builds, and hires for the AI era.',
] as const

export const HERO = {
  sub: 'Sovereign Advisory keeps owners and founders current on AI with a standing call, builds the systems worth building, and finds the people to run them.',
  emailLabel: 'or email hello@joinsov.com',
} as const

export const DRAWING = {
  caption: 'An illustrative drawing.',
  business: 'Your business',
  accessibleName: 'Advise: your judgment. Build: your systems. Hire: your team. Your business.',
  inputs: [
    { id: 'advise', name: 'Advise', detail: 'your judgment' },
    { id: 'build', name: 'Build', detail: 'your systems' },
    { id: 'hire', name: 'Hire', detail: 'your team' },
  ],
} as const

export const NAV_LINKS = [
  { label: 'Advise', href: '/#advise' },
  { label: 'Build', href: '/#build' },
  { label: 'Hire', href: '/#hire' },
  { label: 'About', href: '/#about' },
] as const

export const FIELD_NOTES_LINK = { label: 'Field notes', href: '/#field-notes' } as const

export const LANES_HEADING = 'Three ways in.'

export const LANES = [
  {
    id: 'advise',
    href: '/#advise',
    name: 'Advise',
    title: 'Stay current. Decide well.',
    body: 'A standing call with someone who builds with AI every day, so you know what is real, what is noise, and what to do next.',
  },
  {
    id: 'build',
    href: '/#build',
    name: 'Build',
    title: 'Systems for the businesses we know.',
    body: 'Real estate, bookkeeping, and recruiting. We start with what you already run, and sometimes the answer is to fix it, not replace it.',
  },
  {
    id: 'hire',
    href: '/#hire',
    name: 'Hire',
    title: 'The people to run it.',
    body: 'Fractional talent acquisition for startups: technical and non-technical roles, the process, and the tools.',
  },
] as const

export const LANES_LINE =
  'One idea under all three: own your AI, your systems, and your team instead of renting them.'

export const ADVISE = {
  heading: 'Like a business coach, but for AI.',
  intro:
    'Most owners do not need another tool. They need someone in their corner who keeps them current, pressure-tests the next decision, and tells them when to say no.',
  terms: 'Month to month. Cancel anytime.',
  coversHeading: 'What a call covers',
  covers: [
    'What changed this month that matters to your business',
    'The tool or vendor you are being sold, and whether it is real',
    'The next workflow worth automating, and the one to leave alone',
    'How your team is actually using AI',
  ],
} as const

export type Tier = {
  id: string
  name: string
  price: string
  points: readonly string[]
}

export const ADVISORY_TIERS: readonly Tier[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$350/mo',
    points: [
      'One 60-minute call each month',
      'Questions answered between calls',
      'A short written recap after each call',
    ],
  },
  {
    id: 'weekly',
    name: 'Weekly',
    price: '$800/mo',
    points: [
      'A 60-minute call every week',
      'Faster answers between calls',
      'A quarterly review of your tools and AI spend',
    ],
  },
]

export const AGENT_TOOLS = {
  heading: 'AI agent tools, set up for your team.',
  body: 'Grok Bot, ChatGPT and dots, or a private open-source equivalent you own. We set it up around how your team actually works: which switches to turn on, which to leave off, and proof they held.',
  logosLabel: 'Tools we set up for your team',
  logos: [
    {
      alt: 'Grok',
      lightSrc: '/brands/Grok_Wordmark_Dark.svg',
      darkSrc: '/brands/Grok_Wordmark_Light.svg',
      height: 24,
    },
    {
      alt: 'OpenAI',
      lightSrc: '/brands/OAI_OpenAI_Wordmark_Black.svg',
      darkSrc: '/brands/OAI_OpenAI_Wordmark_White.svg',
      height: 46,
    },
  ],
} as const

export const DISCLAIMER =
  'Sovereign Advisory is independent and not affiliated with or endorsed by xAI or OpenAI. Product names and logos are trademarks of their owners.'

export const BUILD = {
  heading: 'Systems for the businesses we know.',
  intro:
    'We build and tune systems for three kinds of business we have worked inside: real estate, bookkeeping, and recruiting. We start with what you already run. Sometimes the right answer is to fix it, not replace it.',
  teamHeading: 'An AI team for the busywork.',
  teamIntro:
    'We build a set of AI roles around how your business already runs, or show your own team how to build them. Each one works inside rules you set, and anything that reaches a customer goes through a person. This is the team running at one brokerage today.',
  publishHeading: 'We publish what we build.',
  priceLine: 'Every build is quoted for the job.',
  mailLabel: 'Tell us what you run on',
  mailSubject: 'What we run on',
} as const

export type ProofRow = {
  id: string
  vertical: string
  title: string
  body: string
  href?: string
  linkLabel?: string
  features?: readonly { label: string; body?: string; status?: string }[]
  enabled: boolean
}

export const AI_TEAM: readonly { role: string; body: string }[] = [
  {
    role: 'The call reviewer',
    body: 'Listens to every call, writes a summary tied to what was actually said, and keeps track of what was promised.',
  },
  {
    role: 'The deal watcher',
    body: 'Watches every contract deadline and flags the deal that has gone quiet, before it turns into a problem.',
  },
  {
    role: 'The market analyst',
    body: 'Builds CMAs from county and MLS records, then checks each estimate against the real sale price.',
  },
  {
    role: 'The content writer',
    body: 'Drafts local market articles and neighborhood pages, and checks every fact against a source.',
  },
  {
    role: 'The operations assistant',
    body: 'Answers your questions from your own data and proposes next steps. It never acts on its own.',
  },
]

export const BUILD_PROOF: readonly ProofRow[] = [
  {
    id: 'brokerage',
    vertical: 'Real estate',
    title: "A brokerage's daily operating platform",
    body: 'One Florida brokerage runs on it every day. We co-own and run it.',
    features: [
      {
        label: 'Every call, understood',
        status: 'Live',
        body: 'Every call on every line is recorded, transcribed, and summarized, with each point tied to what was said. Promises made on a call are tracked, the call is logged in your CRM automatically, and if a line ever stops recording, an alarm goes off. Nothing slips through the cracks.',
      },
      {
        label: 'Every deal, watched',
        status: 'Live',
        body: 'Each contract becomes a live file: deadlines read straight from the executed contract, each deal paced against how your past closings actually ran, and an alert when a deal goes quiet or a deadline gets close.',
      },
      {
        label: 'CMAs with the evidence attached',
        status: 'Live',
        body: 'A branded, seller-ready CMA built from county sales records and MLS comps, with the flood zone, school zones in supported counties, a price range instead of a single guess, and a seller net sheet at three prices. Every estimate is checked against the real sale price once the home closes.',
      },
      {
        label: 'A blog that keeps your site current',
        status: 'Live',
        body: 'AI drafts local market articles, checks every fact against a source, and stages each post for a final look before it goes live. More than 40 posts are live today.',
      },
      {
        label: "Community pages that answer buyers' questions",
        status: 'Live',
        body: 'Answer-first pages for the communities you sell in, written around what buyers actually ask.',
      },
      {
        label: 'Social posts from the same work',
        status: 'Coming soon',
        body: "Each article turned into posts for each platform, checked against that platform's rules before anything is published.",
      },
      {
        label: 'Outreach with a person in charge',
        body: 'Follow-up runs behind a gate that checks opt-outs, quiet hours, and daily limits, and nothing goes out until a person launches it.',
      },
    ],
    enabled: true,
  },
  {
    id: 'bookkeeping',
    vertical: 'Bookkeeping',
    title: 'A 40-person online bookkeeping firm',
    body: 'They had been quoted a full rebuild. We measured how the firm actually works first, then delivered in fixed-price stages, for far less than the rebuild would have cost.',
    enabled: true,
  },
  {
    id: 'proofmark',
    vertical: 'Recruiting',
    title: 'Proofmark',
    body: 'A recruiting platform for data-center talent. It finds companies that just raised money, watches their job boards every day, and drafts outreach for a person to approve. Nobody types a company name by hand. Deployed and running.',
    enabled: true,
  },
  {
    id: 'hhs',
    vertical: 'Real estate',
    title: 'Human Hits Send',
    body: 'A hands-on Grok Bot class and monthly subscription for real estate agents.',
    href: 'https://humanhitssend.com',
    linkLabel: 'Built by Sovereign',
    enabled: false,
  },
]

export type Repo = {
  name: string
  href: string
  body: string
}

export const OSS_REPOS: readonly Repo[] = [
  {
    name: 'copyability-bench',
    href: 'https://github.com/ryanmitchellneely/copyability-bench',
    body: 'A tok/s number is not a decode rate. Measure whether your serving stack is exploiting output copyability.',
  },
  {
    name: 'glm53-tensorfold-spark',
    href: 'https://github.com/ryanmitchellneely/glm53-tensorfold-spark',
    body: 'A large open model on two NVIDIA DGX Spark machines with a custom inference engine. Work in progress.',
  },
]

export const HIRE = {
  heading: 'Fractional talent acquisition for startups.',
  intro:
    'Your first hires decide what the company becomes. We run the searches, build the process, and set up the tools, for technical and non-technical roles, without a full-time head of talent on the payroll.',
  takesHeading: 'What we take on',
  takes: [
    'Searches for engineering, product, design, and go-to-market roles',
    'An interview process, and interviewers calibrated to it',
    'An applicant tracking system set up to work for you',
    'A hiring plan tied to your budget and runway',
  ],
  recordHeading: 'Track record',
  record: [
    'Twenty years of business strategy, technical, and recruiting experience',
    'Founded a technical recruiting firm, later acquired by Affirm',
    'Built a product management recruiting function from zero at Indeed',
    'Cut engineering time-to-hire from 120 days to 45',
    'Calibrated more than 1,000 interviewers',
    'Peaked at 150 hires in a single quarter',
  ],
  priceLine: 'Scoped per engagement.',
  mailLabel: 'Tell us who you need',
  mailSubject: 'Hiring help',
} as const

export const FIELD_NOTES = {
  heading: 'Field notes.',
  intro: 'Short videos on what we are building and what we are learning.',
} as const

export type VideoNote = {
  title: string
  youtubeId: string
  date: string
}

export const VIDEOS: readonly VideoNote[] = []

export const ABOUT = {
  heading: 'Who you will work with.',
  body: 'Sovereign Advisory is an AI advisory, build, and talent firm for owners and founders. Co-founder Ryan Neely brings twenty years of business strategy, technical, and recruiting experience. He founded a recruiting firm that Affirm acquired, ran hiring operations and product strategy at Indeed, and today leads AI enablement for a public-company talent acquisition team.',
} as const

export const CLOSE = {
  heading: 'Bring us your business.',
  body: 'Tell us what you run on, who you need, or what you are trying to decide. The first call is free, takes 30 minutes, and is about your business, not a pitch.',
} as const

export const FOOTER = {
  legal: '© 2026 Sovereign Advisory.',
  privacy: 'Privacy',
  terms: 'Terms',
  privacyHref: '/privacy.html',
  termsHref: '/terms.html',
} as const
